import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';
import { avisoDeComprobacion, fechaCorta } from '../lib/saludEnlaces';
import { PageHeader, SectionCard } from '../components/Common';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, PlayCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Comprobaciones diarias de la clínica.
 *
 * DOS VIGILANTES, UNA PANTALLA (2026-09-10). El primero, de 2026-09-06
 * (dirección: «que se revise que los enlaces que reciben los pacientes funcionan
 * correctamente… ha habido fallos»), recorre la clínica DESDE FUERA con los
 * ojos del paciente. El segundo, de 2026-09-10 (dirección: «otro agente que hace
 * la tarea del secretario, como si pasara un día de consulta viendo las cosas»),
 * la recorre POR DENTRO con los de recepción.
 *
 * Se enseñan en la MISMA pantalla, en dos pestañas, y no en dos páginas: lo que
 * hay que ver es idéntico —la última pasada con cada comprobación, su detalle y
 * sus ms, el histórico de las diez anteriores y el botón «Comprobar ahora» para
 * dirección—, quien mira una suele querer mirar la otra, y así el menú, la
 * campana y el enlace de Configuración siguen llevando a un solo sitio. La
 * pestaña se recuerda en la URL (`?vista=recepcion`), que es adonde apunta el
 * correo de aviso de recepción.
 */

const VISTAS = [
  {
    clave: 'enlaces',
    etiqueta: 'Enlaces de pacientes',
    endpoint: '/api/salud/enlaces',
    titulo: 'Enlaces de pacientes',
    subtitulo: 'Cada día laborable, a primera hora, el servidor recorre como paciente todos los enlaces que mandamos (confirmar cita, documento único de casa, cuestionario de salud, guía de cirugía, paquete online y modo tablet) con un paciente de prueba que se borra solo.',
    verde: 'Todos los enlaces funcionan.',
    rojo: 'Hay enlaces que fallan: mira el detalle.',
  },
  {
    clave: 'recepcion',
    etiqueta: 'Día de consulta',
    endpoint: '/api/salud/recepcion',
    titulo: 'Día de consulta (recepción)',
    subtitulo: 'Cada día laborable, a las ocho de la mañana, el servidor pasa un día de consulta con los ojos de recepción: entra, abre la agenda, da una cita, prepara los mensajes, marca al paciente, cobra, imprime los papeles, sube una foto, mira el parte de quirófano y exporta el Excel del mes. Con un paciente de prueba que se borra solo.',
    verde: 'El día de consulta sale entero.',
    rojo: 'Hay algo del día de consulta que falla: mira el detalle.',
  },
];

export default function SaludEnlacesPage() {
  const [params, setParams] = useSearchParams();
  const pedida = params.get('vista');
  const vista = VISTAS.find((v) => v.clave === pedida) || VISTAS[0];

  return (
    <div>
      <div className="flex gap-1 mb-4 border-b border-border" data-testid="salud-pestanas">
        {VISTAS.map((v) => (
          <button
            key={v.clave}
            type="button"
            onClick={() => setParams(v.clave === VISTAS[0].clave ? {} : { vista: v.clave })}
            className={`px-3 py-2 text-sm font-medium -mb-px border-b-2 ${
              v.clave === vista.clave
                ? 'border-[#2E6E5E] text-[#13231E]'
                : 'border-transparent text-muted-foreground hover:text-[#13231E]'}`}
            data-testid={`salud-pestana-${v.clave}`}
            aria-current={v.clave === vista.clave ? 'page' : undefined}
          >
            {v.etiqueta}
          </button>
        ))}
      </div>
      <Comprobacion key={vista.clave} vista={vista} />
    </div>
  );
}

/** Una comprobación: su última pasada, su histórico y su «Comprobar ahora». */
function Comprobacion({ vista }) {
  const { user } = useAuth();
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [esperando, setEsperando] = useState(false);
  const atAntes = useRef(null);
  const sondeo = useRef(null);

  const cargar = useCallback(() => api.get(vista.endpoint)
    .then((r) => { setDatos(r.data || { ultima: null, anteriores: [] }); setError(''); return r.data; })
    .catch(() => { setError('No se pudo leer el estado de las comprobaciones.'); return null; }), [vista.endpoint]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => () => { if (sondeo.current) clearInterval(sondeo.current); }, []);

  const comprobarAhora = async () => {
    try {
      const r = await api.post(`${vista.endpoint}/ejecutar`);
      toast.success(r.data?.ya_estaba
        ? 'Ya había una comprobación en marcha; espera a que termine.'
        : 'Comprobación lanzada. Tarda uno o dos minutos: el resultado aparecerá aquí.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo lanzar la comprobación');
      return;
    }
    atAntes.current = datos?.ultima?.at || null;
    setEsperando(true);
    let intentos = 0;
    if (sondeo.current) clearInterval(sondeo.current);
    sondeo.current = setInterval(async () => {
      intentos += 1;
      const d = await cargar();
      const nueva = d?.ultima?.at && d.ultima.at !== atAntes.current;
      if ((nueva && !d.en_marcha) || intentos >= 60) {
        clearInterval(sondeo.current);
        sondeo.current = null;
        setEsperando(false);
        if (nueva) toast[d.ultima.ok ? 'success' : 'error'](d.ultima.ok ? vista.verde : vista.rojo);
      }
    }, 5000);
  };

  const ultima = datos?.ultima || null;
  const aviso = datos ? avisoDeComprobacion(datos, vista.etiqueta) : null;
  const puedeLanzar = isManagement(user);

  return (
    <div>
      <PageHeader
        title={vista.titulo}
        subtitle={vista.subtitulo}
        actions={puedeLanzar && (
          <Button onClick={comprobarAhora} disabled={esperando || !!datos?.en_marcha}
            className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="salud-enlaces-comprobar">
            {(esperando || datos?.en_marcha)
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Comprobando…</>
              : <><PlayCircle className="w-4 h-4 mr-2" /> Comprobar ahora</>}
          </Button>
        )}
      />

      {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}
      {aviso && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-900"
          data-testid="salud-enlaces-aviso">
          {aviso.texto}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <SectionCard title="Última ejecución" className="xl:col-span-2">
          {!datos && !error && <div className="text-sm text-muted-foreground">Cargando…</div>}
          {datos && !ultima && (
            <div className="text-sm text-muted-foreground" data-testid="salud-enlaces-vacio">
              Todavía no se ha hecho ninguna comprobación.
            </div>
          )}
          {ultima && (
            <>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm mb-3" data-testid="salud-enlaces-cabecera">
                <Estado ok={ultima.ok} grande />
                <span>{fechaCorta(ultima.at)}</span>
                <span className="text-muted-foreground">versión {ultima.version || '—'}</span>
                <span className="text-muted-foreground">{Math.round((ultima.duracion_ms || 0) / 1000)} s</span>
                {ultima.rapido && <span className="text-muted-foreground">(pasada rápida tras el despliegue)</span>}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="salud-enlaces-tabla">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                      <th className="py-1.5 pr-2 w-8"></th>
                      <th className="py-1.5 pr-3">Comprobación</th>
                      <th className="py-1.5 pr-3">Detalle</th>
                      <th className="py-1.5 text-right">ms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ultima.checks || []).map((c) => (
                      <tr key={c.nombre} className={`border-b border-border/60 ${c.ok ? '' : 'bg-red-50'}`} data-testid="salud-enlaces-fila">
                        <td className="py-1.5 pr-2"><Estado ok={c.ok} /></td>
                        <td className="py-1.5 pr-3 text-[#13231E]">{c.nombre}</td>
                        <td className={`py-1.5 pr-3 ${c.ok ? 'text-muted-foreground' : 'text-red-800 font-medium'}`}>{c.detalle || ''}</td>
                        <td className="py-1.5 text-right tabular-nums text-muted-foreground">{c.ms}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SectionCard>

        <SectionCard title="Ejecuciones anteriores">
          {datos && (datos.anteriores || []).length === 0 && (
            <div className="text-sm text-muted-foreground">Sin historial todavía.</div>
          )}
          <ul className="divide-y divide-border" data-testid="salud-enlaces-historico">
            {(datos?.anteriores || []).map((e) => (
              <li key={e.id} className="py-2 text-sm flex items-start gap-2">
                <Estado ok={e.ok} />
                <div className="min-w-0">
                  <div className="text-[#13231E]">{fechaCorta(e.at)} <span className="text-muted-foreground">· {e.version || '—'}{e.rapido ? ' · rápida' : ''}</span></div>
                  {e.ok
                    ? <div className="text-xs text-muted-foreground">{e.total} comprobaciones en verde</div>
                    : <div className="text-xs text-red-800">{(e.fallos || []).join(' · ')}</div>}
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}

function Estado({ ok, grande = false }) {
  const cls = grande ? 'w-5 h-5' : 'w-4 h-4';
  return ok
    ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className={cls} />{grande && <span className="font-semibold">Todo en verde</span>}</span>
    : <span className="inline-flex items-center gap-1 text-red-700"><XCircle className={cls} />{grande && <span className="font-semibold">Con fallos</span>}</span>;
}
